import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";

/**
 * Context for managing PowerBI button navigation state
 * Provides a clean way to share navigation state between hook instances
 * without relying on module-level mutable state
 */
const PowerBINavigationContext = createContext(null);

/**
 * Provider component for PowerBI navigation context
 * Manages navigation state and report reference for alignment score extraction
 */
export const PowerBINavigationProvider = ({ children }) => {
  const [navigationTitle, setNavigationTitle] = useState("");
  const reportRef = useRef(null);

  // Callback to update report reference
  const updateReport = useCallback((report) => {
    reportRef.current = report;
  }, []);

  // Callback to trigger navigation
  const triggerNavigation = useCallback((title) => {
    setNavigationTitle(title);
  }, []);

  // Reset navigation title after it's been consumed
  const resetNavigation = useCallback(() => {
    setNavigationTitle("");
  }, []);

  // Memoize the value object to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      navigationTitle,
      reportRef,
      updateReport,
      triggerNavigation,
      resetNavigation,
    }),
    [navigationTitle, updateReport, triggerNavigation, resetNavigation]
  );

  return (
    <PowerBINavigationContext.Provider value={value}>
      {children}
    </PowerBINavigationContext.Provider>
  );
};

PowerBINavigationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access PowerBI navigation context
 * @returns {object} Navigation context value
 * @throws {Error} If used outside of PowerBINavigationProvider
 */
export const usePowerBINavigationContext = () => {
  const context = useContext(PowerBINavigationContext);

  if (!context) {
    throw new Error(
      "usePowerBINavigationContext must be used within a PowerBINavigationProvider"
    );
  }

  return context;
};

export default PowerBINavigationContext;
